import { API_HEAD } from '@/lib/apiConfig';

export interface User {
  id: string;
  name: string;
  email: string;
  phone?: string | null;
  role: 'admin' | 'staff';
  status: 'active' | 'inactive';
  joinDate: string;
  lastLogin: string;
  avatar?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface UserResponse {
  success: boolean;
  data: User;
  message?: string;
}

export interface UsersResponse {
  success: boolean;
  data: {
    users: User[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalItems: number;
      itemsPerPage: number;
    };
  };
}

export interface UserStatsResponse {
  success: boolean;
  data: {
    totalUsers: number;
    activeUsers: number;
    inactiveUsers: number;
    adminUsers: number;
    staffUsers: number;
  };
}

export interface CreateUserData {
  name: string;
  email: string;
  phone?: string | null;
  role?: 'admin' | 'staff';
  status?: 'active' | 'inactive';
}

export interface UpdateUserData {
  name?: string;
  email?: string;
  phone?: string | null;
  role?: 'admin' | 'staff';
  status?: 'active' | 'inactive';
}

export interface GetUsersParams {
  page?: number;
  limit?: number;
  search?: string;
  role?: string;
  status?: string;
}

class UserService {
  private getAuthToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('adminToken');
  }

  private getHeaders(): HeadersInit {
    const token = this.getAuthToken();
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  async getUsers(params: GetUsersParams = {}): Promise<UsersResponse> {
    const searchParams = new URLSearchParams();
    
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.search) searchParams.append('search', params.search);
    if (params.role && params.role !== 'all') searchParams.append('role', params.role);
    if (params.status && params.status !== 'all') searchParams.append('status', params.status);

    const response = await fetch(`${API_HEAD}admin/users?${searchParams.toString()}`, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Lỗi khi tải danh sách người dùng');
    }

    return response.json();
  }

  async getUserById(id: string): Promise<UserResponse> {
    const response = await fetch(`${API_HEAD}admin/users/${id}`, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Lỗi khi tải thông tin người dùng');
    }

    return response.json();
  }

  async createUser(userData: CreateUserData): Promise<UserResponse> {
    const response = await fetch(`${API_HEAD}admin/users`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Lỗi khi tạo người dùng');
    }

    return response.json();
  }

  async updateUser(id: string, userData: UpdateUserData): Promise<UserResponse> {
    const response = await fetch(`${API_HEAD}admin/users/${id}`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Lỗi khi cập nhật người dùng');
    }

    return response.json();
  }

  async deleteUser(id: string): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${API_HEAD}admin/users/${id}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Lỗi khi xóa người dùng');
    }

    return response.json();
  }

  async getUserStats(): Promise<UserStatsResponse> {
    const response = await fetch(`${API_HEAD}admin/users/stats/overview`, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Lỗi khi tải thống kê người dùng');
    }

    return response.json();
  }

  // Lấy danh sách người dùng trong thùng rác
  async getTrashUsers(params: GetUsersParams = {}): Promise<UsersResponse> {
    const searchParams = new URLSearchParams();
    
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.search) searchParams.append('search', params.search);
    if (params.role && params.role !== 'all') searchParams.append('role', params.role);
    if (params.status && params.status !== 'all') searchParams.append('status', params.status);

    const response = await fetch(`${API_HEAD}admin/users/trash?${searchParams.toString()}`, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Lỗi khi tải danh sách người dùng đã xóa');
    }

    return response.json();
  }

  // Khôi phục người dùng
  async restoreUser(id: string): Promise<UserResponse> {
    const response = await fetch(`${API_HEAD}admin/users/${id}/restore`, {
      method: 'PATCH',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        throw new Error('Lỗi khi khôi phục người dùng');
      }
      throw new Error(errorData.message || 'Lỗi khi khôi phục người dùng');
    }

    return response.json();
  }

  // Xóa vĩnh viễn người dùng
  async permanentDeleteUser(id: string): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${API_HEAD}admin/users/${id}/permanent`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Lỗi khi xóa vĩnh viễn người dùng');
    }

    return response.json();
  }
}

export default new UserService();
